import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { OrderApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { useContent } from '../ContentContext';
import { PageHero } from '../components/Common';
import { downloadInvoice } from '../utils/invoice';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmt = (d) => new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

// Fulfilment steps a normal order moves through (cancelled is handled apart).
const STEPS = [
  { id: 'placed', label: 'Order Placed', icon: 'fa-receipt' },
  { id: 'confirmed', label: 'Confirmed', icon: 'fa-circle-check' },
  { id: 'packed', label: 'Packed', icon: 'fa-box' },
  { id: 'shipped', label: 'Shipped', icon: 'fa-truck' },
  { id: 'out_for_delivery', label: 'Out for Delivery', icon: 'fa-truck-fast' },
  { id: 'delivered', label: 'Delivered', icon: 'fa-house-circle-check' },
];

export default function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, booting } = useAuth();
  const { company } = useContent();

  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!booting && !isLoggedIn) navigate('/login', { replace: true });
  }, [booting, isLoggedIn, navigate]);

  const load = useCallback(
    () => OrderApi.get(id).then((d) => setOrder(d.order)).catch((e) => setError(e.message)),
    [id]
  );

  useEffect(() => { if (isLoggedIn) load(); }, [isLoggedIn, load]);

  const cancel = async () => {
    const reason = window.prompt('Reason for cancellation (optional):') ?? '';
    if (!window.confirm('Cancel this order?')) return;
    setCancelling(true);
    try {
      await OrderApi.cancel(id, reason);
      await load();
    } catch (e) { setError(e.message); } finally { setCancelling(false); }
  };

  if (booting || (!order && !error)) {
    return <><PageHero title="Order" /><section><div className="container"><p className="muted">Loading order…</p></div></section></>;
  }
  if (error) {
    return (
      <>
        <PageHero title="Order" />
        <section><div className="container empty-state">
          <i className="fas fa-circle-exclamation" />
          <h3>{error}</h3>
          <Link className="btn-gold" to="/account">Back to My Orders</Link>
        </div></section>
      </>
    );
  }

  const cancelled = order.status === 'cancelled';
  const currentIdx = STEPS.findIndex((s) => s.id === order.status);
  // Timeline entries keyed by status for their timestamps.
  const stamps = {};
  (order.timeline || []).forEach((t) => { stamps[t.status] = t.at; });

  const canCancel = ['placed', 'confirmed', 'packed'].includes(order.status);

  return (
    <>
      <PageHero title={`Order ${order.orderNumber}`} />
      <section>
        <div className="container order-detail-layout">
          <div>
            {/* Tracking */}
            <div className="panel-card">
              <div className="row-between">
                <h3><i className="fas fa-truck-fast" /> Order Tracking</h3>
                <span className={`status ${order.status}`}>{String(order.status).replace(/_/g, ' ')}</span>
              </div>

              {cancelled ? (
                <div className="cancel-note">
                  <i className="fas fa-circle-xmark" /> This order was cancelled
                  {order.cancelReason ? ` — ${order.cancelReason}` : ''}.
                  {order.refundStatus && order.refundStatus !== 'none'
                    ? ` Refund: ${order.refundStatus}.`
                    : ''}
                </div>
              ) : (
                <ol className="track">
                  {STEPS.map((s, i) => {
                    const done = i <= currentIdx;
                    const active = i === currentIdx;
                    return (
                      <li key={s.id} className={`track-step ${done ? 'done' : ''} ${active ? 'active' : ''}`}>
                        <span className="track-dot"><i className={`fas ${s.icon}`} /></span>
                        <div className="track-text">
                          <strong>{s.label}</strong>
                          {stamps[s.id] ? <span className="muted small">{fmt(stamps[s.id])}</span> : null}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}

              {order.shipment?.trackingId ? (
                <div className="ship-box">
                  <i className="fas fa-location-crosshairs" />
                  <span>
                    {order.shipment.carrier ? `${order.shipment.carrier} · ` : ''}
                    Tracking <strong>{order.shipment.trackingId}</strong>
                  </span>
                  {order.shipment.url ? (
                    <a href={order.shipment.url} target="_blank" rel="noreferrer" className="link-btn inline">Track</a>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* Items */}
            <div className="panel-card">
              <h3>Items</h3>
              {order.items.map((it, i) => (
                <div className="order-item" key={i}>
                  {it.image ? <img src={it.image} alt={it.title} /> : <div className="product-noimg" style={{ height: 60, width: 60 }} />}
                  <div style={{ flex: 1 }}>
                    <strong>{it.title}</strong>
                    <p className="muted small">{it.weight} · Qty {it.qty}</p>
                  </div>
                  <strong>{inr(it.price * it.qty)}</strong>
                </div>
              ))}
            </div>

            {/* Address */}
            <div className="panel-card">
              <h3>Delivery Address</h3>
              <p>
                <strong>{order.address?.fullName}</strong><br />
                {[order.address?.line1, order.address?.line2, order.address?.city, order.address?.state, order.address?.pincode].filter(Boolean).join(', ')}<br />
                +91 {order.address?.phone}
              </p>
            </div>
          </div>

          {/* Summary + actions */}
          <aside className="bill-card">
            <h3>Summary</h3>
            <div className="bill-row"><span>Item total</span><span>{inr(order.itemsTotal)}</span></div>
            {order.discount > 0 ? (
              <div className="bill-row"><span>Discount{order.couponCode ? ` (${order.couponCode})` : ''}</span><span className="ok">− {inr(order.discount)}</span></div>
            ) : null}
            <div className="bill-row"><span>Delivery</span><span>{order.deliveryFee ? inr(order.deliveryFee) : <span className="ok">FREE</span>}</span></div>
            <div className="bill-total"><span>Total Paid</span><span>{inr(order.totalAmount)}</span></div>

            <p className="muted small" style={{ marginTop: 10 }}>
              Payment: {order.paymentMethod === 'online' ? 'Online' : 'COD'}
              {' · '}<span className={order.paymentStatus === 'paid' ? 'ok' : ''}>{order.paymentStatus}</span>
            </p>

            <button className="consult-btn" style={{ width: '100%', justifyContent: 'center', marginTop: 14 }} onClick={() => downloadInvoice(order, company)}>
              <i className="fas fa-file-arrow-down" /> Download Invoice
            </button>

            {canCancel ? (
              <button className="link-btn danger" disabled={cancelling} onClick={cancel} style={{ display: 'block', margin: '12px auto 0' }}>
                {cancelling ? 'Cancelling…' : 'Cancel Order'}
              </button>
            ) : null}

            <Link className="link-btn" to="/account" style={{ display: 'block', textAlign: 'center' }}>Back to My Orders</Link>
          </aside>
        </div>
      </section>
    </>
  );
}
